import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchPlayersQuery } from "../../store/api/mlbApi";
import { useSearchNbaPlayersQuery } from "../../store/api/nbaApi";
import { useSearchNflPlayersQuery } from "../../store/api/nflApi";
import { getActiveNbaFranchises } from "../../utils/nbaFranchises";
import { getActiveNflFranchises } from "../../utils/nflFranchises";
import { listCurrentMlbTeams } from "../../utils/teamMlbId";
import { formatPlayerName } from "../../utils/textUtils";
import { MIN_DIRECTORY_QUERY_LENGTH } from "../playerSearch/playerSearchCatalog";
import TeamLogo from "../TeamLogo";
import PlayerPickRow from "../fanPage/PlayerPickRow";
import { colors } from "../../theme";
import { fanHubDialogPaperSx, sportAccent } from "./fanHubStyles";
import FanHubSportPills from "./FanHubSportPills";

const teamsForSport = (sport, query) => {
  const needle = query.trim().toLowerCase();
  const rows =
    sport === "nba"
      ? getActiveNbaFranchises().map((team) => ({
          teamId: team.id,
          name: team.name,
        }))
      : sport === "nfl"
        ? getActiveNflFranchises().map((team) => ({
            teamId: team.id,
            name: team.name,
          }))
        : listCurrentMlbTeams();
  return rows.filter((team) =>
    !needle ? true : team.name.toLowerCase().includes(needle)
  );
};

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const useSportPlayerSearch = (sport, query, enabled) => {
  const skip = !enabled || query.trim().length < MIN_DIRECTORY_QUERY_LENGTH;
  const mlb = useSearchPlayersQuery(query, { skip: skip || sport !== "mlb" });
  const nba = useSearchNbaPlayersQuery(query, { skip: skip || sport !== "nba" });
  const nfl = useSearchNflPlayersQuery(query, { skip: skip || sport !== "nfl" });
  const raw =
    sport === "nba" ? nba.data : sport === "nfl" ? nfl.data : mlb.data;
  return (raw || [])
    .map((player) => ({
      ...player,
      playerId: player.playerId || player.id,
      name: formatPlayerName(player),
    }))
    .filter((player) => player.playerId)
    .slice(0, 12);
};

const PlayerResults = ({ sport, players, onSelect }) => (
  <Box sx={{ maxHeight: 360, overflow: "auto" }}>
    {players.map((player) => (
      <PlayerPickRow
        key={player.playerId}
        player={player}
        sport={sport}
        onClick={() => onSelect(player)}
      />
    ))}
    {players.length === 0 && (
      <Typography color="text.secondary" variant="body2" sx={{ px: 1, py: 2 }}>
        Type at least two letters.
      </Typography>
    )}
  </Box>
);

export const FanHubTeamPicker = ({ open, onClose, onSelect }) => {
  const [sport, setSport] = useState("mlb");
  const [query, setQuery] = useState("");
  const teams = useMemo(() => teamsForSport(sport, query), [sport, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSport("mlb");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: fanHubDialogPaperSx(sportAccent(sport)) }}
    >
      <DialogTitle>Add a favorite team</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 0.5 }}>
          <FanHubSportPills
            value={sport}
            onChange={(next) => {
              setSport(next);
              setQuery("");
            }}
          />
        </Box>
        <TextField
          autoFocus
          fullWidth
          label="Search teams"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ maxHeight: 360, overflow: "auto" }}>
          {teams.map((team) => (
            <Box
              key={team.teamId}
              component="button"
              type="button"
              onClick={() => {
                onSelect({ sport, teamId: team.teamId, name: team.name });
                onClose();
              }}
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.5,
                px: 1,
                py: 1,
                border: 0,
                bgcolor: "transparent",
                color: "inherit",
                font: "inherit",
                borderRadius: 1.5,
                cursor: "pointer",
                textAlign: "left",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <TeamLogo
                sport={sport}
                teamName={team.name}
                franchiseId={team.teamId}
                size={28}
              />
              <Typography>{team.name}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export const FanHubPlayerPicker = ({ open, onClose, onSelect }) => {
  const [sport, setSport] = useState("mlb");
  const [query, setQuery] = useState("");
  const search = useDebouncedValue(query);
  const players = useSportPlayerSearch(sport, search, open);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSport("mlb");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: fanHubDialogPaperSx(sportAccent(sport)) }}
    >
      <DialogTitle>Follow a player</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 0.5 }}>
          <FanHubSportPills
            value={sport}
            onChange={(next) => {
              setSport(next);
              setQuery("");
            }}
          />
        </Box>
        <TextField
          autoFocus
          fullWidth
          label="Search players"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 2 }}
        />
        <PlayerResults
          sport={sport}
          players={players}
          onSelect={(player) => {
            onSelect({ sport, playerId: player.playerId, name: player.name });
            onClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export const FanHubComparePicker = ({ open, onClose, onSave }) => {
  const [sport, setSport] = useState("mlb");
  const [query, setQuery] = useState("");
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const search = useDebouncedValue(query);
  const players = useSportPlayerSearch(sport, search, open);
  const accent = sportAccent(sport);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSport("mlb");
      setLeft(null);
      setRight(null);
    }
  }, [open]);

  const pick = (player) => {
    if (!left) setLeft(player);
    else if (!right && player.playerId !== left.playerId) setRight(player);
    setQuery("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: fanHubDialogPaperSx(accent) }}
    >
      <DialogTitle>Save a comparison</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 0.5 }}>
          <FanHubSportPills
            value={sport}
            onChange={(next) => {
              setSport(next);
              setQuery("");
              setLeft(null);
              setRight(null);
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Player one
            </Typography>
            <Typography fontWeight={700}>{left?.name || "Pick a player"}</Typography>
          </Box>
          <Typography fontWeight={800} sx={{ color: accent }}>
            VS
          </Typography>
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Player two
            </Typography>
            <Typography fontWeight={700}>{right?.name || "Pick a player"}</Typography>
          </Box>
        </Box>
        <TextField
          fullWidth
          label={!left ? "Search player one" : "Search player two"}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 2 }}
        />
        <PlayerResults sport={sport} players={players} onSelect={pick} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!left || !right}
          onClick={() => {
            onSave({
              sport,
              playerIds: [left.playerId, right.playerId],
              title: `${left.name} vs ${right.name}`,
            });
            onClose();
          }}
        >
          Save comparison
        </Button>
      </DialogActions>
    </Dialog>
  );
};
